import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStores } from '../../../stores';
import partnerServiceService from '../../../services/partnerServiceService';
import partnerService from '../../../services/partnerService';
import {
  Card,
  Row,
  Col,
  Button,
  Input,
  Select,
  Tag,
  Space,
  Modal,
  message,
  Empty,
  Spin,
  Badge,
  Dropdown,
  Alert,
  Progress,
  Typography
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  EuroOutlined,
  MoreOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ShopOutlined
} from '@ant-design/icons';
import './Services.scss';

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;

const calculateTotalSlots = (availabilities, durationMinutes, capacity = 1) => {
  if (!availabilities || !availabilities.length || !durationMinutes) {
    return 0;
  }

  let totalSlots = 0;

  availabilities.forEach(avail => {
    if (!avail.start_time || !avail.end_time) return;

    const [startHours, startMins] = avail.start_time.split(':').map(Number);
    const [endHours, endMins] = avail.end_time.split(':').map(Number);

    const startMinutes = startHours * 60 + startMins;
    const endMinutes = endHours * 60 + endMins;

    const availDuration = endMinutes - startMinutes;

    if (availDuration > 0) {
      const slots = Math.floor(availDuration / durationMinutes) * capacity;
      totalSlots += slots;
    }
  });

  return totalSlots;
};

const ServicesList = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { partnerServiceStore } = useStores();
  const { filteredServices, loading, searchQuery, categoryFilter, activeFilter } = partnerServiceStore;

  const [serviceCategories, setServiceCategories] = useState([]);
  const [itemsInfo, setItemsInfo] = useState(null);

  useEffect(() => {
    partnerServiceStore.fetchServices();
    fetchItemsInfo();

    const fetchCategories = async () => {
      const result = await partnerServiceService.getServiceCategories();
      if (result.success) {
        setServiceCategories(result.data);
      }
    };

    fetchCategories();
  }, [partnerServiceStore]);

  const fetchItemsInfo = async () => {
    const result = await partnerService.fetchItemsInfo();
    if (result.success) {
      setItemsInfo(result.data);
    }
  };

  const isLimitReached = itemsInfo && itemsInfo.items_remaining <= 0;
  const usagePercent = itemsInfo ? (itemsInfo.current_items / itemsInfo.max_items) * 100 : 0;

  const handleSearch = (value) => {
    partnerServiceStore.setSearchQuery(value);
  };

  const handleCategoryFilter = (value) => {
    partnerServiceStore.setCategoryFilter(value);
  };

  const handleActiveFilter = (value) => {
    partnerServiceStore.setActiveFilter(value === 'all' ? null : value === 'active');
  };

  const handleDeleteService = (serviceId, serviceName) => {
    Modal.confirm({
      title: t('common.delete'),
      content: t('partner.services.deleteConfirm'),
      okText: t('common.delete'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: async () => {
        const result = await partnerServiceStore.deleteService(serviceId);
        if (result.success) {
          message.success(t('partner.services.messages.deleteSuccess'));
        } else {
          message.error(result.error);
        }
      }
    });
  };

  const handleToggleActive = async (serviceId, currentStatus) => {
    const result = await partnerServiceStore.updateService(serviceId, { is_active: !currentStatus });
    if (result.success) {
      message.success(t('partner.services.messages.toggleSuccess'));
    } else {
      message.error(result.error);
    }
  };

  const getMenuItems = (service) => [
    {
      key: 'edit',
      icon: <EditOutlined />,
      label: t('partner.services.editService'),
      onClick: () => navigate(`/partner/services/edit/${service.id}`)
    },
    {
      key: 'availability',
      icon: <ClockCircleOutlined />,
      label: t('partner.services.manageAvailability'),
      onClick: () => navigate(`/partner/services/${service.id}/availability`)
    },
    {
      key: 'holidays',
      icon: <CalendarOutlined />,
      label: t('partner.services.manageHolidays'),
      onClick: () => navigate(`/partner/services/${service.id}/holidays`)
    },
    {
      key: 'toggle',
      icon: service.is_active ? <CloseCircleOutlined /> : <CheckCircleOutlined />,
      label: service.is_active ? t('partner.services.deactivate') : t('partner.services.activate'),
      onClick: () => handleToggleActive(service.id, service.is_active)
    },
    {
      type: 'divider'
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: t('common.delete'),
      danger: true,
      onClick: () => handleDeleteService(service.id, service.name_en)
    }
  ];

  return (
    <div className="services-list">
      <div className="services-list-header">
        <h1>{t('partner.services.title')}</h1>
        {!isLimitReached ? (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/partner/services/add')}
          >
            {t('partner.services.addService')}
          </Button>
        ) : (
          <Button
            type="primary"
            icon={<ShopOutlined />}
            onClick={() => navigate('/partner/packages')}
          >
            {t('partner.products.getMoreSlots')}
          </Button>
        )}
      </div>

      {/* Item Usage Info */}
      {itemsInfo && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <div style={{ flex: 1, maxWidth: 300 }}>
              <Text type="secondary">{t('partner.products.itemUsage')}</Text>
              <Progress
                percent={Math.round(usagePercent)}
                status={isLimitReached ? 'exception' : usagePercent >= 80 ? 'normal' : 'active'}
                strokeColor={isLimitReached ? '#ff4d4f' : usagePercent >= 80 ? '#faad14' : '#1890ff'}
                size="small"
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {itemsInfo.current_items} / {itemsInfo.max_items} {t('partner.products.itemsLabel')}
              </Text>
            </div>
            {itemsInfo.items_remaining <= 3 && itemsInfo.items_remaining > 0 && (
              <Text type="warning">
                {itemsInfo.items_remaining} {t('partner.products.slotsRemaining')}
              </Text>
            )}
          </Space>
        </Card>
      )}

      {/* Limit Reached Alert */}
      {isLimitReached && (
        <Alert
          message={t('partner.products.limitReached')}
          description={
            <span>
              {t('partner.products.limitReachedDesc')}{' '}
              <Link to="/partner/packages">{t('partner.products.getMoreSlots')}</Link>
            </span>
          }
          type="warning"
          showIcon
          icon={<ShopOutlined />}
          style={{ marginBottom: 16 }}
        />
      )}

      <Card className="filters-card">
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder={t('partner.services.searchPlaceholder')}
              allowClear
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col xs={24} sm={6} md={4}>
            <Select
              placeholder={t('common.category')}
              allowClear
              style={{ width: '100%' }}
              value={categoryFilter}
              onChange={handleCategoryFilter}
            >
              {serviceCategories.map(cat => (
                <Option key={cat.id} value={cat.id}>{cat.name_en}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={6} md={4}>
            <Select
              placeholder={t('common.status')}
              style={{ width: '100%' }}
              value={activeFilter === null ? 'all' : (activeFilter ? 'active' : 'inactive')}
              onChange={handleActiveFilter}
            >
              <Option value="all">{t('common.all')}</Option>
              <Option value="active">{t('common.active')}</Option>
              <Option value="inactive">{t('common.inactive')}</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      {loading ? (
        <div className="loading-container">
          <Spin size="large" />
        </div>
      ) : filteredServices.length === 0 ? (
        <Empty
          description={t('partner.services.noServices')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          {!isLimitReached ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/partner/services/add')}>
              {t('partner.services.addFirstService')}
            </Button>
          ) : (
            <Button type="primary" icon={<ShopOutlined />} onClick={() => navigate('/partner/packages')}>
              {t('partner.products.getMoreSlots')}
            </Button>
          )}
        </Empty>
      ) : (
        <Row gutter={[16, 16]} className="services-grid">
          {filteredServices.map(service => (
            <Col xs={24} sm={12} lg={8} key={service.id}>
              <Badge.Ribbon
                text={service.is_active ? t('common.active') : t('common.inactive')}
                color={service.is_active ? 'green' : 'red'}
              >
                <Card
                  className="service-card"
                  actions={[
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => navigate(`/partner/services/edit/${service.id}`)}
                    >
                      {t('common.edit')}
                    </Button>,
                    <Button
                      type="text"
                      icon={<ClockCircleOutlined />}
                      onClick={() => navigate(`/partner/services/${service.id}/availability`)}
                    >
                      {t('partner.services.availability')}
                    </Button>,
                    <Dropdown menu={{ items: getMenuItems(service) }} trigger={['click']}>
                      <Button type="text" icon={<MoreOutlined />} />
                    </Dropdown>
                  ]}
                >
                  <div className="service-card-content">
                    <Tag color="blue" className="category-tag">
                      {service.service_category_name || t('common.service')}
                    </Tag>

                    <h3 className="service-name">{service.name_en}</h3>
                    <p className="service-name-greek">{service.name_el}</p>

                    <div className="service-details">
                      <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <div className="detail-item">
                          <ClockCircleOutlined /> <span>{service.duration} {t('common.minutes')}</span>
                        </div>
                        <div className="detail-item">
                          <TeamOutlined /> <span>{t('partner.services.capacityLabel', { capacity: service.capacity })}</span>
                        </div>
                        <div className="detail-item">
                          <EuroOutlined /> <span>{parseFloat(service.price).toFixed(2)}</span>
                        </div>
                        <div className="detail-item">
                          <CalendarOutlined />
                          <span>
                            {calculateTotalSlots(
                              service.availabilities,
                              service.duration_minutes || service.duration,
                              service.capacity || 1
                            )} {t('partner.services.bookingSlotsLabel', { slots: '' }).replace('{{slots}}', '').trim()}
                          </span>
                        </div>
                      </Space>
                    </div>
                  </div>
                </Card>
              </Badge.Ribbon>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
});

export default ServicesList;
